import { z } from 'zod'
import { RESULT_TOKEN_IDENTITY_HEADER } from 'fundraising-core'
import { QUIZ_TOKEN_FRESH_SECONDS } from 'fundraising-core/thank-you-quiz'
import { PERSONAL_RESULT_PARAMS, firstValue } from 'refresh-common'

/**
 * Who the supporter is, after a form, without their details in a URL. Tracking pixels record the
 * thank-you URL, so personal params never travel there (`PERSONAL_RESULT_PARAMS` in refresh-common
 * is the one list, #2158). Every interact form's server route hands the donor over as the `dr_rt`
 * result-token cookie instead — the same encrypted, HttpOnly token primary-site uses
 * (fundraising-server `typedDonorCookie`) — and pages read it back through the fundraising
 * router's `result-token-donor` route. `whId` still greets a donor who arrived by a personal link.
 */

export { PERSONAL_RESULT_PARAMS, omitPersonalParams } from 'refresh-common'

/** The second-ask link's marker: "prefill me from the token". It carries nothing personal. */
export const PREFILL_PARAM = 'prefill'

/** What a page takes from the token, in the query-param names the legacy forms read. */
export interface ResultDonor {
  fname?: string
  lname?: string
  email?: string
  mobile?: string
  countryCode?: string
}

// Past level 1 the route's mobile is the CRM's, masked ("•••••• 1234") — never a prefill.
const isMasked = (value: string) => /[•*]/.test(value)

/**
 * How recent a token must be to greet or prefill. Every interact form's submit mints one just
 * before its thank-you page and second ask, so anything older was left by an earlier visit — on a
 * shared device, possibly by someone else. Long enough for a card or Swish payment round trip.
 */
export const TOKEN_FRESH_SECONDS = QUIZ_TOKEN_FRESH_SECONDS

const zTokenDonorResponse = z.object({
  success: z.literal(true),
  // Seconds since the token was issued, by the server's clock. Absent: treated as stale.
  age: z.number().optional(),
  donor: z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    mobile: z.string().optional(),
    countryCode: z.string().optional(),
  }),
})

type Fetcher = (url: string, init: RequestInit) => Promise<Response>

/**
 * The donor behind the `dr_rt` result-token cookie. HttpOnly, so only the server can read it, and it
 * survives a payment app reopening the thank-you page in a new tab. No token, one older than
 * `TOKEN_FRESH_SECONDS`, no secret on this deploy, or any failure: nothing, and the page shows its
 * generic greeting and an unprefilled form.
 */
export const readTokenDonor = async (fetcher: Fetcher = fetch): Promise<ResultDonor> => {
  try {
    const res = await fetcher('/api/fundraising/result-token-donor', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [RESULT_TOKEN_IDENTITY_HEADER]: '1' },
      body: '{}',
    })
    if (!res.ok) return {}
    const parsed = zTokenDonorResponse.safeParse(await res.json())
    if (!parsed.success) return {}
    const { age } = parsed.data
    if (age === undefined || age > TOKEN_FRESH_SECONDS) return {}
    const { firstName, lastName, email, mobile, countryCode } = parsed.data.donor
    const donor: ResultDonor = {
      fname: firstName,
      lname: lastName,
      email,
      mobile: mobile && !isMasked(mobile) ? mobile : undefined,
      countryCode,
    }
    return Object.fromEntries(Object.entries(donor).filter(([, v]) => v)) as ResultDonor
  } catch {
    return {}
  }
}

/** True when a query carries any personal param; such a link describes its own visit. */
export const hasPersonalParams = (query: Record<string, unknown>): boolean =>
  PERSONAL_RESULT_PARAMS.some(key => !!firstValue(query[key] as string | string[] | undefined))

/**
 * Whether a form should prefill from the token: only when it was reached from a second ask (the
 * marker), exactly as it used to prefill only from a second ask's URL params — never on an ordinary
 * visit, where the token may belong to whoever used this device last. A link that still carries
 * personal params wins.
 */
export const shouldPrefillFromToken = (query: Record<string, unknown>): boolean =>
  firstValue(query[PREFILL_PARAM] as string | string[] | undefined) === '1' &&
  !hasPersonalParams(query)

/** The part of interact's `Person` a prefill touches. */
export interface PrefillablePerson {
  name: { first: string; last: string }
  contacts: { email: string; mobile: string }
  address: { countryCode?: string }
}

/**
 * The token's country replaces the page's default (`SE`) — a form reached without personal URL
 * params has nothing of the donor's own in it — but never one the donor picked while the token was
 * on its way: `countryAtStart` is what the form held before it was read.
 */
const fillCountry = (
  address: { countryCode?: string },
  donor: ResultDonor,
  countryAtStart: string | undefined,
) => {
  if (donor.countryCode && address.countryCode === countryAtStart) {
    address.countryCode = donor.countryCode
  }
}

/** Fill the fields the form doesn't have yet — never over what the donor has typed. */
export const fillPerson = (
  person: PrefillablePerson,
  donor: ResultDonor,
  countryAtStart = person.address.countryCode,
): void => {
  person.name.first ||= donor.fname ?? ''
  person.name.last ||= donor.lname ?? ''
  person.contacts.email ||= donor.email ?? ''
  person.contacts.mobile ||= donor.mobile ?? ''
  fillCountry(person.address, donor, countryAtStart)
}

/** The part of a company form a prefill touches — what those forms took from the URL. */
export interface PrefillableCompany {
  email: string
  address: { countryCode?: string }
}

export const fillCompany = (
  company: PrefillableCompany,
  donor: ResultDonor,
  countryAtStart = company.address.countryCode,
): void => {
  company.email ||= donor.email ?? ''
  fillCountry(company.address, donor, countryAtStart)
}

/**
 * The thank-you page's greeting name, first match wins: the CRM's first name for a donor who came
 * by a personal link (`whId`); a name the result URL itself still carries (an old link describes
 * its own result); else the donor behind a fresh result token. Whole sources, never mixed.
 */
export const greetingName = (
  crmFirstName: string | undefined,
  query: { fname?: string | null },
  token: ResultDonor,
): string => crmFirstName || query.fname || token.fname || ''
